package dev.akshaychordiya.components.layouts

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.css.TextDecorationLine
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.data.getValue
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobwebx.markdown.markdown
import dev.akshaychordiya.MetaTextStyle
import dev.akshaychordiya.toSitePalette
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.Div

/**
 * Article metadata lifted out of front matter.
 *
 * @param originalUrl Where the piece first appeared, if it did. Shown as an acknowledgement rather than hidden -
 *   these were on Medium for years and readers may recognise them.
 */
class ArticleData(
    val title: String,
    val date: String,
    val description: String,
    val originalUrl: String?,
    val originalName: String?,
)

@InitRoute
fun initArticleLayout(ctx: InitRouteContext) {
    val frontMatter = ctx.markdown!!.frontMatter
    val title = frontMatter["title"]?.firstOrNull()
    require(title != null) { "Article markdown must set \"title\" in its front matter" }
    val date = frontMatter["date"]?.firstOrNull()
    require(date != null) { "Article markdown must set \"date\" in its front matter (yyyy-MM-dd)" }

    ctx.data.add(
        ArticleData(
            title = title,
            date = date,
            description = frontMatter["description"]?.firstOrNull().orEmpty(),
            originalUrl = frontMatter["originalUrl"]?.firstOrNull(),
            originalName = frontMatter["originalName"]?.firstOrNull(),
        )
    )
    ctx.data.add(PageLayoutData(title))
}

@Composable
@Layout(".components.layouts.PageLayout")
fun ArticleLayout(ctx: PageContext, content: @Composable () -> Unit) {
    val article = ctx.data.getValue<ArticleData>()
    val palette = ColorMode.current.toSitePalette()

    Column(Modifier.fillMaxWidth().margin(top = 1.5.cssRem)) {
        Link(
            "/articles",
            "← All articles",
            MetaTextStyle.toModifier().margin(bottom = 1.5.cssRem),
            variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
        )

        SpanText(
            article.title,
            Modifier.fontSize(2.3.cssRem).fontWeight(700).lineHeight(1.15).letterSpacing((-0.04).cssRem),
        )

        Row(Modifier.gap(0.5.cssRem).margin(top = 0.75.cssRem, bottom = 2.cssRem).flexWrap(FlexWrap.Wrap)) {
            SpanText(formatDate(article.date), MetaTextStyle.toModifier())
            if (article.originalUrl != null) {
                SpanText("·", MetaTextStyle.toModifier())
                Link(
                    article.originalUrl,
                    "Originally on ${article.originalName ?: "Medium"}",
                    MetaTextStyle.toModifier().color(palette.brandPrimary),
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                )
            }
        }

        Div(ProseStyle.toAttrs()) {
            content()
        }

        // Both hydrated by `public/site.js`. Deliberately empty placeholders rather than Compose components: they
        // need fetch, clipboard and the Web Share API, and keeping that in plain JS means they work without waiting
        // for the Kotlin/JS bundle - and that no-JS readers see nothing rather than dead controls.
        Div(ArticleFooterStyle.toAttrs()) {
            Div(Modifier.attrsModifier { id("claps") }.toAttrs())
            Div(ShareMountStyle.toModifier().attrsModifier { id("share") }.toAttrs())
        }
    }
}

/** Claps and share, side by side once there's room and stacked before that. */
val ArticleFooterStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .margin(top = 3.cssRem)
            .padding(top = 1.5.cssRem)
            .borderTop(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .gap(1.25.cssRem)
    }
    Breakpoint.SM {
        Modifier
            .flexDirection(FlexDirection.Row)
            .alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.SpaceBetween)
    }
}

val ShareMountStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .alignItems(AlignItems.Center)
        .gap(0.5.cssRem)
        .flexWrap(FlexWrap.Wrap)
}

/**
 * Styles for the JS-created clap button.
 *
 * These have to be registered as plain CSS rules because the element doesn't exist at compile time - `site.js` builds
 * it. Registering them here rather than in a stylesheet keeps them theme-aware, since they can read the palette.
 *
 * The `clap-pop` keyframes live in the raw CSS block in `build.gradle.kts`, alongside the reduced-motion rule that
 * flattens them.
 */
@InitSilk
fun initClapStyles(ctx: InitSilkContext) {
    ColorMode.entries.forEach { mode ->
        val palette = mode.toSitePalette()
        val scope = "html.silk-${mode.name.lowercase()}"

        ctx.stylesheet.registerStyleBase("$scope .clap-button") {
            Modifier
                .display(DisplayStyle.LegacyInlineFlex)
                .alignItems(AlignItems.Center)
                .gap(0.5.cssRem)
                .padding(topBottom = 0.55.cssRem, leftRight = 1.1.cssRem)
                .borderRadius(2.cssRem)
                .fontSize(0.95.cssRem)
                .fontWeight(600)
                .cursor(Cursor.Pointer)
                .backgroundColor(palette.brandPrimary.toRgb().copyf(alpha = 0.08f))
                .color(palette.brandPrimary)
                .border(1.px, LineStyle.Solid, palette.brandPrimary.toRgb().copyf(alpha = 0.3f))
                .styleModifier {
                    property("transition", "transform 200ms cubic-bezier(0.16,1,0.3,1), background-color 200ms")
                    // Anchors the floating "+1" below, so it travels with the button wherever the layout puts it.
                    property("position", "relative")
                }
        }
        // The "+1" that drifts up on each clap. Absolute so it never nudges the button's own layout.
        ctx.stylesheet.registerStyleBase("$scope .clap-plus") {
            Modifier
                .color(palette.brandPrimary)
                .fontSize(0.85.cssRem)
                .fontWeight(700)
                .styleModifier {
                    property("position", "absolute")
                    property("top", "-0.2rem")
                    property("right", "0.6rem")
                    property("pointer-events", "none")
                    property("animation", "clap-rise 800ms cubic-bezier(0.16, 1, 0.3, 1) forwards")
                }
        }
        ctx.stylesheet.registerStyleBase("$scope .clap-button:hover:not(:disabled)") {
            Modifier
                .translateY((-2).px)
                .backgroundColor(palette.brandPrimary.toRgb().copyf(alpha = 0.16f))
        }
        ctx.stylesheet.registerStyleBase("$scope .clap-button:disabled") {
            Modifier.cursor(Cursor.Default).opacity(0.65)
        }
        ctx.stylesheet.registerStyleBase("$scope .clap-count") {
            // Tabular figures stop the button resizing as the count ticks 9 -> 10.
            Modifier.styleModifier { property("font-variant-numeric", "tabular-nums") }
        }
        ctx.stylesheet.registerStyleBase("$scope .share-label") {
            Modifier.fontSize(0.85.cssRem).color(palette.subdued)
        }
        ctx.stylesheet.registerStyleBase("$scope .share-button") {
            Modifier
                .display(DisplayStyle.InlineBlock)
                .padding(topBottom = 0.4.cssRem, leftRight = 0.8.cssRem)
                .borderRadius(2.cssRem)
                .fontSize(0.85.cssRem)
                .fontWeight(500)
                .cursor(Cursor.Pointer)
                .backgroundColor(palette.background)
                .color(palette.subdued)
                .border(1.px, LineStyle.Solid, palette.border)
                .textDecorationLine(TextDecorationLine.None)
                .styleModifier { property("transition", "color 200ms, border-color 200ms") }
        }
        ctx.stylesheet.registerStyleBase("$scope .share-button:hover") {
            Modifier.color(palette.brandPrimary).border(1.px, LineStyle.Solid, palette.brandPrimary)
        }
    }

    ctx.stylesheet.registerStyleBase(".clap-icon") {
        Modifier.display(DisplayStyle.InlineBlock).fontSize(1.1.cssRem)
    }
    // Named keyframes so the global prefers-reduced-motion rule flattens this like every other animation.
    ctx.stylesheet.registerStyleBase(".clap-icon.pop") {
        Modifier.styleModifier { property("animation", "clap-pop 320ms cubic-bezier(0.16, 1, 0.3, 1)") }
    }
}

private val MONTHS = listOf(
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
)

/**
 * Turns `2020-03-29` into `29 March 2020`. Falls back to the raw string if the date isn't in the expected shape,
 * so a typo in front matter degrades to something readable instead of crashing the page.
 */
fun formatDate(iso: String): String {
    val parts = iso.split("-")
    if (parts.size != 3) return iso
    val year = parts[0]
    val month = parts[1].toIntOrNull() ?: return iso
    val day = parts[2].toIntOrNull() ?: return iso
    if (month !in 1..12) return iso
    return "$day ${MONTHS[month - 1]} $year"
}
